/* global axios */
import ApiClient from './ApiClient';

export const buildContactParams = (page, sortAttr, label, search) => {
  let params = `include_contact_inboxes=false&page=${page}&sort=${sortAttr}`;
  if (search) {
    params = `${params}&q=${encodeURIComponent(search)}`;
  }
  if (label) {
    params = `${params}&labels[]=${encodeURIComponent(label)}`;
  }
  return params;
};

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  get(page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}?${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  show(id) {
    return axios.get(`${this.url}/${id}?include_contact_inboxes=false`);
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}?include_contact_inboxes=false`, data);
  }

  getConversations(contactId, { inboxId } = {}) {
    const params = inboxId ? { inbox_id: inboxId } : {};
    return axios.get(`${this.url}/${contactId}/conversations`, { params });
  }

  getAttendanceProtocols(contactId) {
    return axios.get(`${this.url}/${contactId}/conversations/protocols`);
  }

  getAttachments(contactId, page = 1) {
    return axios.get(`${this.url}/${contactId}/attachments`, {
      params: { page },
    });
  }

  getContactableInboxes(contactId) {
    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  initiateCall(contactId, inboxId, conversationId = null) {
    return axios.post(`${this.url}/${contactId}/call`, {
      inbox_id: inboxId,
      conversation_id: conversationId,
    });
  }

  updateContactLabels(contactId, labels) {
    return axios.post(`${this.url}/${contactId}/labels`, { labels });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '', options = {}) {
    let requestURL = `${this.url}/search?${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;
    return axios.get(requestURL, { signal: options.signal });
  }

  active(page = 1, sortAttr = 'name') {
    let requestURL = `${this.url}/active?${buildContactParams(page, sortAttr)}`;
    return axios.get(requestURL);
  }

  getMap() {
    return axios.get(`${this.url}/map`);
  }

  syncMap() {
    return axios.post(`${this.url}/map_sync`);
  }

  // eslint-disable-next-line default-param-last
  filter(page = 1, sortAttr = 'name', queryPayload) {
    let requestURL = `${this.url}/filter?${buildContactParams(page, sortAttr)}`;
    return axios.post(requestURL, queryPayload);
  }

  importContacts({ file, columnMapping = {}, labels = [], delimiter = ',' }) {
    const formData = new FormData();
    formData.append('import_file', file);
    formData.append('column_mapping', JSON.stringify(columnMapping));
    formData.append('delimiter', delimiter);
    labels.forEach(label => formData.append('labels[]', label));
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  importHistory(dataType = 'contacts') {
    return axios.get(
      '/api/v1/accounts/' + this.accountIdFromRoute + '/data_imports',
      { params: { data_type: dataType } }
    );
  }

  validateConversationMigration(formData) {
    return axios.post(
      '/api/v1/accounts/' +
        this.accountIdFromRoute +
        '/data_imports/validate_conversation_migration',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  startConversationMigration(formData) {
    return axios.post(
      '/api/v1/accounts/' +
        this.accountIdFromRoute +
        '/data_imports/conversation_migration',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }

  exportContactConversations(contactId, params) {
    return axios.get(`${this.url}/${contactId}/conversations_export`, {
      params,
      responseType: 'arraybuffer',
    });
  }
}

export default new ContactAPI();
